import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { ledgerOptions, listVoucherTypes, type NoteKind, type VoucherFull, type VoucherType } from '@/api/accountsBooks';
import { ReceiptPaymentForm } from './ReceiptPaymentForm';
import { ContraForm } from './ContraForm';
import { JournalForm } from './JournalForm';
import { SalesForm } from './SalesForm';
import { PurchaseForm } from './PurchaseForm';
import { NoteForm } from './NoteForm';
import { ExpenseForm } from './ExpenseForm';
import { TdsChallanForm } from './TdsChallanForm';
import { withoutVoucher } from './voucherFormat';

/** Opens the form for a voucher type, laid out as the prototype, once its ledgers and next number are in. */
export function VoucherFormModal({ type, voucher, onClose, kind, startChallan, startExpense }: { type: VoucherType; voucher: VoucherFull | null; onClose: () => void; kind?: NoteKind; startChallan?: boolean; startExpense?: boolean }) {
  const ledgersQ = useQuery({ queryKey: ['acc-books', 'ledger-options'], queryFn: ledgerOptions });
  // Journal: General or Expense bill. An expense bill being altered opens on its own form.
  const [expense, setExpense] = useState(voucher?.details?.kind === 'exp' || (!voucher && !!startExpense));
  // Payment: a payment or a TDS challan. A challan being altered opens on its own form.
  const [challan, setChallan] = useState((voucher?.details as { kind?: string } | null)?.kind === 'tdsc' || (!voucher && !!startChallan));
  const typesQ = useQuery({ queryKey: ['acc-books', 'voucher-types'], queryFn: listVoucherTypes });

  if (!ledgersQ.data || !typesQ.data) {
    return (
      <Modal open onClose={onClose} title={`${voucher ? 'Alter' : 'New'} ${type}`} size="sm">
        <p className="text-sm text-[var(--text-muted)]">{ledgersQ.isError || typesQ.isError ? 'Couldn’t load the ledgers — close and try again.' : 'Loading…'}</p>
      </Modal>
    );
  }
  const props = { voucher, ledgers: withoutVoucher(ledgersQ.data, voucher), nextNo: typesQ.data.find((t) => t.name === type)?.nextNo ?? '—', onClose };
  if (expense) return <ExpenseForm {...props} onGeneral={voucher ? undefined : () => setExpense(false)} />;
  if (challan) return <TdsChallanForm {...props} onGeneral={voucher ? undefined : () => setChallan(false)} />;
  if (type === 'Receipt' || type === 'Payment') return <ReceiptPaymentForm type={type} {...props} onChallan={type === 'Payment' && !voucher ? () => setChallan(true) : undefined} />;
  if (type === 'Contra') return <ContraForm {...props} />;
  if (type === 'Sales') return <SalesForm {...props} />;
  if (type === 'Purchase') return <PurchaseForm {...props} />;
  if (type === 'Credit Note' || type === 'Debit Note') return <NoteForm {...props} kind={kind ?? (type === 'Debit Note' ? 'dn' : 'cn')} />;
  return <JournalForm {...props} onExpense={voucher ? undefined : () => setExpense(true)} />;
}
